import { forwardRef, ReactElement, ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { Collapse } from '@mui/material';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';

export type TLeftMenuItemProps = {
  title: ReactNode;
  titleIcon: ReactElement;
  isOpen: boolean;
  route: string;
};

export default function LeftMenuItem({ title, titleIcon, isOpen, route }: TLeftMenuItemProps) {
  const location = useLocation();

  const renderLink = useMemo(
    () => forwardRef<HTMLAnchorElement>((itemProps, ref) => <Link to={route} ref={ref} {...itemProps} />),
    [route],
  );

  const isSelected = useMemo(() => location.pathname.includes(route), [location.pathname, route]);

  return (
    <ListItem key={route} disablePadding sx={{ mb: 1.5 }}>
      <ListItemButton
        sx={{
          minHeight: 40,
          minWidth: 80,
          justifyContent: 'flex-start',
          px: 0.5,
          py: 1,
        }}
        selected={isSelected}
        component={renderLink}
      >
        <Tooltip disableInteractive={isOpen} title={title} placement="right-start">
          <ListItemIcon
            sx={{
              minWidth: 0,
              px: 3,
              justifyContent: 'center',
            }}
          >
            {titleIcon}
          </ListItemIcon>
        </Tooltip>
        <Collapse orientation="horizontal" in={isOpen}>
          <ListItemText
            primary={title}
            disableTypography
            color="secondary"
            sx={{
              m: 0,
              pr: 3,
              whiteSpace: 'nowrap',
              fontWeight: isSelected ? 700 : 600,
            }}
          />
        </Collapse>
      </ListItemButton>
    </ListItem>
  );
}
